import { useTranslation } from 'react-i18next';
import { useCommunity } from '@/hooks/useCommunity';
import { Post } from '@/models/Post';

/**
 * Returns the translated system text for a post
 * @param post The post to translate
 * @param useRelatedPostType If true and post has RelatedPostType, returns the related post's system text instead
 */
export function useSystemTextTranslation(post: Post, useRelatedPostType = false): string | null {
  const { t } = useTranslation(['domain']);
  const community = useCommunity();

  if (!post.IsSystem || !post.PostType) return null;

  // Base options for interpolation (without namespace)
  const options: { [key: string]: string } = {};

  // Use RelatedPostType for parsing when available (e.g., NotifyNewComment on a system post)
  const parsePostType = post.RelatedPostType || post.PostType;

  // Non-system post types have no system text translation
  const NON_SYSTEM_POST_TYPES = ['UserPost', 'WallToWall', 'NewPhoto'];

  // Determine the translation key
  let key: string;
  if (useRelatedPostType && post.RelatedPostType) {
    // Non-system related post types don't have translation keys;
    // return null so the caller can fall back to displaying the original text
    if (NON_SYSTEM_POST_TYPES.includes(post.RelatedPostType)) {
      return null;
    }
    // Use the related post type for translation (e.g., to show "Changed their location to X" for a comment notification)
    key = `PostType.${post.RelatedPostType}`;
  } else if (post.PostType === 'NotifyNewComment') {
    key = post.PhotoId ? 'PostType.NotifyNewComment.photo' : 'PostType.NotifyNewComment.post';
  } else {
    key = `PostType.${post.PostType}`;
  }

  // Helper function to extract text from nested JSON structure (notifications) or plain text
  const extractText = (text: string): string => {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && 'originalText' in parsed) {
        return parsed.originalText;
      }
      return text;
    } catch {
      return text;
    }
  };

  // Add additional options for specific PostTypes (using parsePostType for parsing logic)
  if (
    (parsePostType === 'ChangedLocation' ||
      parsePostType === 'ChangedNickname') &&
    post.Text
  ) {
    options.text = extractText(post.Text);
  }

  if (parsePostType === 'NewMember' && community) {
    options.communityName = community.name;
  }

  if (parsePostType === 'ChangedWeight' && post.Text) {
    try {
      const extractedText = extractText(post.Text);
      const weightData = JSON.parse(extractedText) as string[];
      if (Array.isArray(weightData) && weightData.length >= 2) {
        options.newWeight = weightData[0];
        options.newWeightDiff = weightData[1];
      }
    } catch (error) {
      console.warn('Failed to parse ChangedWeight JSON data:', error);
    }
  }

  // Return the translated string from domain namespace
  return t(key, options);
}
